import { z } from 'zod'

import type {
  CreateSegmentRequest,
  SegmentsPreview,
  UpdateSegmentRequest,
} from '@/api/segments/segmentsApi'
import type { OptionType, SegmentModel } from '@/types'

export type SegmentModalMode =
  | { type: 'create'; baseSegment?: SegmentModel }
  | { type: 'edit'; segment: SegmentModel }

export type PricingMode = 'own' | 'base'

export const PRICING_MODE_OPTIONS: { value: PricingMode; label: string }[] = [
  { value: 'own', label: 'Свои цены' },
  { value: 'base', label: 'На базе другого сегмента' },
]

const optionSchema = z.object({ value: z.string(), label: z.string() })

export const MARKUP_HINT =
  'Наценка к закупочной цене поставщика, которую получит этот сегмент при загрузке цен из файлов поставщиков. В каждой настройке загрузки её можно изменить. На цены из УТ не влияет.'

export const RRC_HINT =
  'Из этого сегмента берётся рекомендованная розничная цена (РРЦ) во всём ЛК. Сегмент РРЦ может быть только один: при включении флаг снимется с прежнего сегмента.'

export const getRrcChangeMessage = (
  nextDescription: string,
  currentDescription: string
) =>
  `Сегментом РРЦ станет «${nextDescription.trim() || 'новый сегмент'}» вместо «${currentDescription}». Продолжить?`

const MAX_MARKUP_PERCENTAGE = 9999.99
const MARKUP_FRACTION_PATTERN = /^\d+([.,]\d{1,2})?$/

const parsePercentage = (value: string) => {
  const normalized = value.trim().replace(',', '.')
  if (normalized === '') return null
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

export const segmentFormSchema = z
  .object({
    description: z.string().trim().min(1, 'Введите название сегмента'),
    pricingMode: z.enum(['own', 'base']),
    baseSegment: optionSchema.nullable().catch(null),
    priceModifierPercentage: z
      .string()
      .refine(
        (value) => value.trim() === '' || parsePercentage(value) !== null,
        'Введите число'
      ),
    markupPercentage: z
      .string()
      .trim()
      .min(1, 'Укажите наценку')
      .refine((value) => {
        const parsed = parsePercentage(value)
        return (
          MARKUP_FRACTION_PATTERN.test(value) &&
          parsed !== null &&
          parsed <= MAX_MARKUP_PERCENTAGE
        )
      }, 'Введите число от 0 до 9999,99, не больше двух знаков после запятой'),
    isRrc: z.boolean(),
    isDefaultForNewClients: z.boolean(),
  })
  .superRefine((values, ctx) => {
    if (values.pricingMode === 'base' && !values.baseSegment) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['baseSegment'],
        message: 'Выберите базовый сегмент',
      })
    }
  })

export type SegmentFormType = z.infer<typeof segmentFormSchema>

const toBaseSegmentOption = (base: {
  id: string
  description: string
  status?: SegmentModel['status']
}): OptionType => ({
  value: base.id,
  label:
    base.status === 'ARCHIVED'
      ? `${base.description} (В архиве)`
      : base.description,
})

export const buildSegmentFormDefaults = (
  mode: SegmentModalMode
): SegmentFormType => {
  if (mode.type === 'edit') {
    const { segment } = mode
    const base = segment.baseSegmentShortModel
    return {
      description: segment.description,
      pricingMode: base ? 'base' : 'own',
      baseSegment: base ? toBaseSegmentOption(base) : null,
      priceModifierPercentage: String(segment.priceModifierPercentage),
      markupPercentage: String(segment.markupPercentage),
      isRrc: segment.isRrc,
      isDefaultForNewClients: segment.isDefaultForNewClients,
    }
  }

  if (mode.baseSegment) {
    return {
      description: `На базе ${mode.baseSegment.description}`,
      pricingMode: 'base',
      baseSegment: toBaseSegmentOption(mode.baseSegment),
      priceModifierPercentage: '0',
      markupPercentage: String(mode.baseSegment.markupPercentage),
      isRrc: false,
      isDefaultForNewClients: false,
    }
  }

  return {
    description: '',
    pricingMode: 'own',
    baseSegment: null,
    priceModifierPercentage: '0',
    markupPercentage: '0',
    isRrc: false,
    isDefaultForNewClients: false,
  }
}

const getBaseSegmentId = (values: SegmentFormType) =>
  values.pricingMode === 'base' && values.baseSegment
    ? values.baseSegment.value
    : null

export const toCreateSegmentRequest = (
  values: SegmentFormType
): CreateSegmentRequest => {
  const baseSegmentId = getBaseSegmentId(values)
  return {
    description: values.description.trim(),
    baseSegmentId,
    priceModifierPercentage:
      baseSegmentId === null
        ? 0
        : (parsePercentage(values.priceModifierPercentage) ?? 0),
    isDefaultForNewClients: values.isDefaultForNewClients,
    markupPercentage: parsePercentage(values.markupPercentage) ?? 0,
    isRrc: values.isRrc,
  }
}

export const toUpdateSegmentRequest = (
  values: SegmentFormType,
  segment: SegmentModel
): UpdateSegmentRequest => {
  const baseSegmentId = getBaseSegmentId(values)
  return {
    owner: segment.ownerOrganisation,
    description: values.description.trim(),
    baseSegmentId,
    priceModifierPercentage:
      baseSegmentId === null
        ? 0
        : (parsePercentage(values.priceModifierPercentage) ??
          segment.priceModifierPercentage),
    markupPercentage:
      parsePercentage(values.markupPercentage) ?? segment.markupPercentage,
    isForClient: segment.isForClient,
    isActive: segment.isActive,
    ...(values.isRrc && !segment.isRrc ? { isRrc: true } : {}),
  }
}

export const buildBaseSegmentOptions = (
  preview: SegmentsPreview
): OptionType[] => [
  ...preview.myAvailableSegments.map((segment) => ({
    value: segment.id,
    label: segment.description,
  })),
  ...preview.partnerAvailableSegments.map((segment) => ({
    value: segment.id,
    label: segment.supplierShortModel
      ? `${segment.description} (${segment.supplierShortModel.description})`
      : segment.description,
  })),
]
